import { useState } from "react";
import { KeyRound, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createUser, deleteUser, updateUser } from "@/lib/api";

/** Libellés affichés pour chaque rôle. */
const ROLES = [
  { value: "professor", label: "Professeur (professeur référent)" },
  { value: "tutor", label: "Tuteur (entreprise)" },
  { value: "admin", label: "Administrateur" },
];

const roleLabel = (role) => (ROLES.find((item) => item.value === role) || { label: role }).label;
const emptyForm = { name: "", email: "", role: "professor", organization: "", password: "" };

/**
 * Liste des comptes et création de comptes (professeurs, tuteurs, administrateurs).
 * Réservé aux administrateurs : l'affichage est conditionné par le tableau de bord.
 */
export default function AccountsPanel({ users = [], currentUserId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [resetPour, setResetPour] = useState(null);
  const [nouveauMotDePasse, setNouveauMotDePasse] = useState("");
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");

  const maj = (cle, valeur) => setForm((actuel) => ({ ...actuel, [cle]: valeur }));

  const creer = async (event) => {
    event.preventDefault();
    setErreur("");
    try {
      await createUser(form);
      setMessage(`Compte créé : ${form.email}`);
      setForm(emptyForm);
      setOpen(false);
      onChanged?.();
    } catch (error) {
      setErreur(error?.response?.data?.detail || "Création impossible");
    }
  };

  const supprimer = async (compte) => {
    const confirmation = window.confirm(
      `Supprimer le compte de ${compte.name} (${compte.email}) ?\n\nLes étudiants, stages et présences sont conservés.`,
    );
    if (!confirmation) return;
    setErreur("");
    try {
      await deleteUser(compte.id);
      setMessage(`Compte supprimé : ${compte.email}`);
      onChanged?.();
    } catch (error) {
      setErreur(error?.response?.data?.detail || "Suppression impossible");
    }
  };

  const reinitialiser = async (event) => {
    event.preventDefault();
    setErreur("");
    try {
      await updateUser(resetPour.id, { password: nouveauMotDePasse });
      setMessage(`Mot de passe réinitialisé : ${resetPour.email}`);
      setResetPour(null);
      setNouveauMotDePasse("");
      onChanged?.();
    } catch (error) {
      setErreur(error?.response?.data?.detail || "Réinitialisation impossible");
    }
  };

  const parRole = (role) => users.filter((compte) => compte.role === role);

  return (
    <section className="space-y-6" data-testid="accounts-view">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h2 className="font-display text-3xl font-bold" data-testid="accounts-title">Comptes</h2>
          <p className="text-sm text-zinc-500">
            Créez les comptes des professeurs référents et des tuteurs d'entreprise. Un administrateur
            supplémentaire peut être nommé pour partager cette responsabilité.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[#002FA7]" data-testid="account-create-button">
              <UserPlus className="h-4 w-4" /> Ajouter un compte
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouveau compte</DialogTitle>
              <DialogDescription>
                Le mot de passe doit contenir au moins huit caractères. Il pourra être réinitialisé ensuite.
              </DialogDescription>
            </DialogHeader>
            <form className="space-y-3" onSubmit={creer}>
              <div>
                <Label htmlFor="account-name">Nom affiché</Label>
                <Input id="account-name" value={form.name} onChange={(e) => maj("name", e.target.value)} required data-testid="account-name-input" />
              </div>
              <div>
                <Label htmlFor="account-email">Adresse électronique</Label>
                <Input id="account-email" type="email" value={form.email} onChange={(e) => maj("email", e.target.value)} required data-testid="account-email-input" />
              </div>
              <div>
                <Label htmlFor="account-role">Rôle</Label>
                <select
                  id="account-role"
                  className="h-10 w-full border border-zinc-300 bg-white px-3 text-sm"
                  value={form.role}
                  onChange={(e) => maj("role", e.target.value)}
                  data-testid="account-role-select"
                >
                  {ROLES.map((role) => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="account-organization">Établissement ou entreprise</Label>
                <Input id="account-organization" value={form.organization} onChange={(e) => maj("organization", e.target.value)} data-testid="account-organization-input" />
              </div>
              <div>
                <Label htmlFor="account-password">Mot de passe</Label>
                <Input id="account-password" type="text" minLength={8} value={form.password} onChange={(e) => maj("password", e.target.value)} required data-testid="account-password-input" />
              </div>
              {erreur && <p className="text-sm text-red-600">{erreur}</p>}
              <Button type="submit" className="w-full rounded-none bg-[#002FA7]" data-testid="account-submit-button">Créer le compte</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {message && <p className="border-l-4 border-emerald-500 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="accounts-message">{message}</p>}
      {!message && erreur && <p className="border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">{erreur}</p>}

      <div className="grid gap-3 md:grid-cols-3" data-testid="accounts-summary">
        <div className="border border-zinc-200 bg-white p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Administrateurs</p>
          <p className="font-display text-3xl font-bold">{parRole("admin").length}</p>
        </div>
        <div className="border border-zinc-200 bg-white p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Professeurs</p>
          <p className="font-display text-3xl font-bold">{parRole("professor").length}</p>
        </div>
        <div className="border border-zinc-200 bg-white p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Tuteurs</p>
          <p className="font-display text-3xl font-bold">{parRole("tutor").length}</p>
        </div>
      </div>

      <div className="overflow-x-auto border border-zinc-200 bg-white">
        <table className="w-full text-sm" data-testid="accounts-table">
          <thead>
            <tr className="bg-[#002FA7] text-left text-white">
              <th className="p-3">Nom</th>
              <th className="p-3">Adresse</th>
              <th className="p-3">Rôle</th>
              <th className="p-3">Établissement / entreprise</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((compte) => (
              <tr key={compte.id} className="border-b border-zinc-100" data-testid={`account-row-${compte.id}`}>
                <td className="p-3 font-semibold">
                  {compte.name}
                  {compte.id === currentUserId && <span className="ml-2 text-xs text-zinc-500">(vous)</span>}
                </td>
                <td className="p-3 text-zinc-600">{compte.email}</td>
                <td className="p-3">
                  <span className={`inline-flex items-center gap-1 border px-2 py-1 text-xs ${compte.role === "admin" ? "border-[#002FA7] text-[#002FA7]" : "border-zinc-300 text-zinc-600"}`}>
                    {compte.role === "admin" && <ShieldCheck className="h-3 w-3" />}
                    {roleLabel(compte.role)}
                  </span>
                </td>
                <td className="p-3 text-zinc-600">{compte.organization || "—"}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" className="rounded-none" onClick={() => { setResetPour(compte); setNouveauMotDePasse(""); setErreur(""); }} data-testid={`account-reset-${compte.id}`}>
                      <KeyRound className="h-3 w-3" /> Mot de passe
                    </Button>
                    <Button variant="destructive" size="sm" className="rounded-none" onClick={() => supprimer(compte)} disabled={compte.id === currentUserId} data-testid={`account-delete-${compte.id}`}>
                      <Trash2 className="h-3 w-3" /> Supprimer
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {!users.length && (
              <tr>
                <td colSpan={5} className="p-6 text-center text-zinc-500">Aucun compte enregistré.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={Boolean(resetPour)} onOpenChange={(ouvert) => !ouvert && setResetPour(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
            <DialogDescription>
              Nouveau mot de passe pour {resetPour?.name} ({resetPour?.email}). Il devra être communiqué à la personne concernée.
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-3" onSubmit={reinitialiser}>
            <div>
              <Label htmlFor="account-new-password">Nouveau mot de passe</Label>
              <Input id="account-new-password" minLength={8} value={nouveauMotDePasse} onChange={(e) => setNouveauMotDePasse(e.target.value)} required data-testid="account-new-password-input" />
            </div>
            {erreur && <p className="text-sm text-red-600">{erreur}</p>}
            <Button type="submit" className="w-full rounded-none bg-[#002FA7]" data-testid="account-reset-submit">Enregistrer</Button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
